import React, { useEffect, useState } from "react";
import { Autocomplete } from "@mui/material";
import {
	CustomDialog,
	CustomButton,
	CustomTooltip,
	icon,
	Selector,
	IconComponent,
	CustomTextField,
} from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import styles from "./TemplateFormDialog.module.css";
import { downloadFile, isExcelMimeTypes } from "../../../../../../common/utils";
import { useApi } from "../../../../../../common/hooks";
import { BackOfficeService } from "../../../../../../api";
import { translateTemplates } from "../../../../utils/utils";
import { TEMPLATE_TYPES } from "../../constants";

const TemplateFormDialog = ({ selectedRow, companyId, onClose, onCreate, onEdit, open, companies = [] }) => {
	const { call: createExportTemplate } = useApi(BackOfficeService.createExportTemplate);
	const { call: updateExportTemplates } = useApi(BackOfficeService.updateExportTemplates);
	const { call: downloadExportTemplates } = useApi(BackOfficeService.downloadExportTemplates);
	const [name, setName] = useState("");
	const [downloading, setDownloading] = useState(false);
	const [file, setFile] = useState(null);
	const [errors, setErrors] = useState({});
	const [company, setCompany] = useState(null);
	const [templateType, setTemplateType] = useState(TEMPLATE_TYPES.REQUIREMENTS);
	useEffect(() => {
		if (open) {
			if (selectedRow) {
				setName(selectedRow.name);
				setTemplateType(selectedRow.excelTemplateTypeEnum);
			}
			if (companyId) {
				setCompany(companyId);
			}
		}
	}, [open, companyId, selectedRow]);
	const handleClose = () => {
		setName("");
		setFile(null);
		setCompany(null);
		setTemplateType(TEMPLATE_TYPES.REQUIREMENTS);
		setErrors({});
		onClose();
	};
	const handleSubmit = () => {
		const payload = { name };
		if (file) {
			payload.file = file;
		}
		const request = selectedRow
			? updateExportTemplates({ companyId: company, templateId: selectedRow.id }, payload)
			: createExportTemplate({ companyId: company }, { templateType, ...payload });
		request
			.then((data) => {
				if (selectedRow) {
					onEdit(data);
				} else {
					onCreate(data);
				}
				handleClose();
			})
			.catch(console.error);
	};
	const handleChange = (value) => {
		setName(value);
		setErrors((prev) => ({ ...prev, name: value.length < 3 }));
	};
	const handleChangeCompany = (cmp) => {
		setCompany(cmp?.id ?? null);
	};
	const handleOpenFile = (e) => {
		const tempFile = e.target.files[0];
		if (!tempFile) {
			setErrors((prev) => ({ ...prev, file: "no-file" }));
			return;
		}
		setFile(tempFile);
		if (!isExcelMimeTypes(e.target.files[0].type)) {
			setErrors((prev) => ({ ...prev, file: "wrong-type" }));
			return;
		}
		setErrors((prev) => ({ ...prev, file: false }));
	};
	const handleDownload = () => {
		setDownloading(true);
		downloadExportTemplates({ companyId: company, templateId: selectedRow.id })
			.then(({ data, filename }) => downloadFile({ data, filename, filetype: "xlsx" }))
			.catch((err) => console.error(err))
			.finally(() => setDownloading(false));
	};
	return (
		<CustomDialog
			isForm
			open={open}
			size={65}
			submitDisabled={errors.name || errors.file || (!file && !selectedRow) || !name || !company || !templateType}
			title={translate(
				`dsi.export-templates-management.dialog.template.${(!!selectedRow && "edit") || "create"}`
			)}
			onClose={handleClose}
			onSubmit={handleSubmit}
		>
			<div className={styles.content}>
				<CustomTextField
					fullWidth
					required
					error={errors?.name && name.length > 0}
					helperText={
						errors.name && name.length > 0 && translate("common:error.required-with-min-data", { min: 3 })
					}
					placeholder={translate("dsi.export-templates-management.table.header.templates")}
					size="small"
					value={name}
					variant="outlined"
					onChange={(e) => handleChange(e.target.value)}
				/>
				<Autocomplete
					fullWidth
					disabled={!!selectedRow}
					getOptionLabel={(option) => option.companyName}
					options={companies}
					renderInput={(params) => (
						<CustomTextField
							{...params}
							placeholder={translate("dsi.user-details.th.company")}
							size="small"
							variant="outlined"
						/>
					)}
					value={companies.find((c) => c.id === company) ?? null}
					onChange={(_, cmp) => handleChangeCompany(cmp)}
				/>
				<Selector
					fullWidth
					disabled={!!selectedRow}
					itemRenderer={(item) => translateTemplates(item)}
					items={Object.values(TEMPLATE_TYPES)}
					value={templateType}
					valueRenderer={(item) => translateTemplates(item)}
					onChange={(e) => setTemplateType(e.target.value)}
				/>
				<div className={styles.importContainer}>
					<CustomButton color="secondary" component="label" variant="outlined">
						<input hidden accept=".xls, .xlsx" type="file" onChange={handleOpenFile} />
						<IconComponent icon={icon.faDownload} />
						{translate("dsi.export-templates-management.dialog.template.import.btn")}
					</CustomButton>
					<CustomTooltip
						showWhenOverflow
						title={file?.name || translate("dsi.export-templates-management.import-template")}
					>
						<div className={styles.file}>{file?.name}</div>
					</CustomTooltip>
				</div>
				{!!selectedRow && (
					<div>
						<CustomButton
							color="secondary"
							component="label"
							disabled={downloading}
							variant="outlined"
							onClick={handleDownload}
						>
							<IconComponent icon={icon.faUpload} />
							{translate("dsi.export-templates-management.dialog.template.export.btn")}
						</CustomButton>
					</div>
				)}
				{errors.file && (
					<span className={styles.fileError}>
						{translate(`dsi.export-templates-management.import-template.error.${errors.file}`)}
					</span>
				)}
			</div>
		</CustomDialog>
	);
};

export default TemplateFormDialog;
